import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { z } from 'zod';
import { AddressSchema } from './address.types';
import { CustomFieldRecordSchema } from './customFields.types';
import { ContactStatusEnumZ } from './enums.types';

// from /api/accounts/{accountId}/contacts and /api/quotes/{quoteId}->.contact[type]
export const ContactRequestSchema = z.object({
  snapshotId: z.string().nullish(),
  fullName: z.string().max(60).nonempty(getRequiredMessage('Full Name')),
  email: z.email().max(70).nonempty(getRequiredMessage('Email')),
  phone: z.string().max(50).nullish(),
  address: AddressSchema.nullish(),
  customId: z.string().nullish(),
  status: ContactStatusEnumZ,
  title: z.string().max(40).nullish(),
  internal: z.boolean().optional(),
  primary: z.boolean().optional(),
  esigner: z.boolean().optional(),
  defaultESigner: z.boolean().optional(),
  customFields: CustomFieldRecordSchema,
});
export type IContactRequestSchema = z.infer<typeof ContactRequestSchema>;

export const ContactRespSchema = BaseResponseSchema.extend({
  snapshotId: z.string().nullish(),
  accountId: z.string(),
  address: AddressSchema,
  customId: z.string().nullable(),
  email: z.string(),
  fullName: z.string(),
  internal: z.boolean(),
  phone: z.string().nullable(),
  primary: z.boolean(),
  status: ContactStatusEnumZ,
  title: z.string().nullable(),
  esigner: z.boolean().optional(),
  defaultESigner: z.boolean().optional(),
  customFields: CustomFieldRecordSchema,
});
export type IContactRespSchema = z.infer<typeof ContactRespSchema>;

export const ContactBulkRequestSchema = ContactRequestSchema.extend({
  referenceId: z.string(),
});
export type IContactBulkRequestSchema = z.infer<
  typeof ContactBulkRequestSchema
>;

export const BulkContactResponseSchema = z.object({
  response: z.array(
    z.object({
      contactId: z.string().nullable(),
      referenceId: z.string(),
      status: z.enum(['Successful', 'Failed']),
      errorMessages: z.array(z.string()),
    }),
  ),
});
export type IBulkContactResponseSchema = z.infer<
  typeof BulkContactResponseSchema
>;

export const QuoteContactsRequest = z.object({
  esignCcIds: z.string().array(),
  billingId: z.string().nullish(),
  esignIds: z.string().array(),
  primaryId: z.string().nullish(),
  additionalSignerIds: z.string().array(),
});

export type IQuoteContactsRequest = z.infer<typeof QuoteContactsRequest>;

export const QuoteContacts = z.object({
  billing: ContactRespSchema.nullable(),
  esign: z.array(ContactRespSchema),
  primary: ContactRespSchema.nullable(),
  additionalESignRecipients: z.array(ContactRespSchema),
  additionalSigners: z.array(ContactRespSchema),
});

export type IQuoteContacts = z.infer<typeof QuoteContacts>;

export type ContactTypes = keyof Omit<
  IQuoteContacts,
  'additionalESignRecipients'
>;
/**
 * Used for quote contacts
 */
export interface ContactWithTypes {
  contact: IContactRespSchema;
  primary: boolean;
  billing: boolean;
  esign: boolean;
}

/**
 * Used for onetime invoices
 */
export type ContactWithoutEsignTypes = Omit<ContactWithTypes, 'esign'>;

/**
 * Used for processed address
 */
export interface ContactFormattedAddress {
  addressLine1: string;
  addressLine2: string;
  /** City, State Zip */
  otherAddress: string;
  country: string;
  /** City, State Zip, Country */
  cityStatePCCountry: string;
  /** lin1, line2, cityStatePCCountry */
  fullAddress: string;
}
